import { JiraLoginType, InstanceStatus, JiraHosting } from "shared/utils/enums";
import { JiraInstance, JiraInstanceBasic, JiraInstanceOAuth } from "shared/utils/interfaces";
import { ViewMode, Routes } from "./enums";
import { GlobalState } from "config/configureRedux";
import { BaseUrl } from "./currentUrls";
import { Jira } from "@yasoonofficial/runtime";
import { VersionUtil } from "./versionUtil";
import { getUrlParameterByName } from "@yasoonofficial/runtime";
import { StringUtil } from "./stringUtil";

let cacheBusterIncrement = 0;
export type EmailBodyPrefillSetting = "preferUniqueEmail" | "wholeEmail" | "doNotPrefill";
export class Utilities {

    static tryAddHeader(headers: HeadersInit, name: string, value: string): void {
        if (headers instanceof Headers) {
            if (!headers.has(name)) {
                headers.append(name, value);
            }
        } else if (!Array.isArray(headers)) {
            if (!headers[name])
                headers[name] = value;
        }

        return;
    }

    static addCacheBuster(url: string): string {
        let addSign = (url.indexOf('?') > -1) ? '&' : '?';
        cacheBusterIncrement++;
        return url += addSign + '__ca=' + new Date().getTime() + '_' + cacheBusterIncrement;
    }

    static hasJiraLoginInfo(instance: JiraInstance): boolean {
        if (instance?.loginType === JiraLoginType.basic) {
            let basicInstance = instance as JiraInstanceBasic;
            if (basicInstance.login && basicInstance.login.username && basicInstance.login.password) {
                return true;
            }
        } else if (instance?.loginType === JiraLoginType.oauth) {
            let oauthInstance = instance as JiraInstanceOAuth;
            if (oauthInstance.login && oauthInstance.login.oauth_secret && oauthInstance.login.oauth_token) {
                return true;
            }
        }

        return false;
    }

    static isInstanceHealthy(instance: JiraInstance): boolean {
        if (!instance)
            return false;

        switch (instance.status) {
            case InstanceStatus.ok:
            case InstanceStatus.upgradeAvailable:
                return true;
            case InstanceStatus.loginExpired:
            case InstanceStatus.noLogin:
            default:
                return false;
        }
    }

    static hasBufferedInstanceInfo(instance: JiraInstance): boolean {
        if (!instance)
            return false;

        let hasBufferedInstanceInfo: boolean =
            !!instance.systemInfo
            && !!instance.addonSettings;

        if (instance.hosting === JiraHosting.server) {
            hasBufferedInstanceInfo = hasBufferedInstanceInfo && !!instance.pluginInfo;
        }

        return hasBufferedInstanceInfo;
    }

    static hasBufferedSDInfo(instance: JiraInstance): boolean {
        if (!instance)
            return false;

        let hasBufferedSDInfo: boolean =
            !!instance.serviceDeskInfo
            && !!instance.serviceDeskData;

        return hasBufferedSDInfo;
    }

    static hasBufferedLicense(instance: JiraInstance): boolean {
        return !!instance?.license;
    }

    static supportsPaneClose(): boolean {
        return !!Office.context.ui['closeContainer'];
    }

    static tryClosePane(): void {
        if (this.supportsPaneClose())
            Office.context.ui['closeContainer']();

        return;
    }

    static getUniqueKey(): string {
        //Use current time to get something short unique
        const currentTime = Math.round(new Date().getTime() / 1000);
        const buf = new ArrayBuffer(4);
        let view = new DataView(buf);
        view.setUint32(0, currentTime, false);

        let binary = '';
        const bytes = new Uint8Array(buf);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }

        return window.btoa(binary).replace(/=/g, '').replace(/\//g, '').replace(/\+/g, '');
    }

    static parseUrl(url: string): { protocol: string, host: string, pathName: string; } {
        // URL object does not work in IE! So we use link element instead :(
        let el = document.createElement('a');
        el.href = url;

        let host = el.host;
        let pathName = el.pathname;
        let protocol = el.protocol.replace(':', '');
        // typescript-eslint:disable-next-line
        if (el.port == '443' || el.port == '80') {
            host = el.hostname;
        }

        if (pathName.indexOf('/') !== 0) {
            pathName = '/' + pathName;
        }

        return {
            protocol: protocol,
            host: host,
            pathName: pathName
        };
    }

    static getMetaForField(state: GlobalState, key: string): Jira.MetaField | null {
        if (state.ui.currentMeta && state.ui.currentMeta.createMeta && state.ui.currentMeta.createMeta[key])
            return state.ui.currentMeta.createMeta[key];
        else if (state.relatedIssues.currentIssue &&
            state.relatedIssues.currentIssue.issue &&
            state.relatedIssues.currentIssue.issue.editmeta &&
            state.relatedIssues.currentIssue.issue.editmeta.fields[key]
        )
            return state.relatedIssues.currentIssue.issue.editmeta.fields[key];

        console.log("Couldn't find meta for field:", key);
        return null;
    }

    static mapViewModeToRoute(mode: ViewMode): string | void {
        if (mode === ViewMode.createIssue) {
            return Routes.createIssue;
        } else if (mode === ViewMode.addToIssue) {
            return Routes.addToIssue;
        } else if (mode === ViewMode.relatedIssues) {
            return Routes.relatedIssues;
        } else if (mode === ViewMode.firstLaunch) {
            return Routes.firstLaunch;
        } else {
            console.error("There was no Route selected for mode ", mode);
            return;
        }
    }

    static mapUrlParameterToViewMode(type: string): ViewMode | never {
        if (type === 'newEdit') {
            return ViewMode.createIssue;
        } else if (type === 'addToIssue') {
            return ViewMode.addToIssue;
        } else if (type === 'viewIssues') {
            return ViewMode.relatedIssues;
        } else {
            throw new Error('Unknown url parameter type ' + type);
        }
    }

    static mapViewModeToUrlParameter(mode: string): string | never {
        if (mode === ViewMode.createIssue) {
            return 'newEdit';
        } else if (mode === ViewMode.addToIssue) {
            return 'addToIssue';
        } else if (mode === ViewMode.relatedIssues) {
            return 'viewIssues';
        } else {
            throw new Error('Unknown url parameter mode ' + mode);
        }
    }

    static getCurrentViewMode(): ViewMode | never {
        let mode = getUrlParameterByName('mode');
        if (mode) {
            return mode as ViewMode;
        } else {
            let type = getUrlParameterByName('type');
            return Utilities.mapUrlParameterToViewMode(type);
        }
    }

    static safeSetItem(key: string, data: string): void {
        try {
            localStorage.setItem(key, data);
        } catch (e) {
            console.log('Error Setting LocalStorage Item', key, e);
        }

        return;
    }

    static safeGetItem(key: string): string | null {
        try {
            return localStorage.getItem(key);
        } catch (e) {
            console.log('Error Getting LocalStorage Item', key, e);
            return '';
        }
    }

    static safeRemoveItem(key: string): void {
        try {
            localStorage.removeItem(key);
        } catch (e) {
            console.log('Error Removing LocalStorage Item', key, e);
        }

        return;
    }

    static appendToFileName(fileName: string, appendString: string): string {
        //We will add a appendString to a file respecting the file ending.
        //e.g. document.docx --> document-2019-10-01:12:00:00.docx

        let fileParts = fileName.split('.');
        if (fileParts.length < 2) {
            console.log('Cannot change filename without ending:', fileName);
            return fileName;
        }

        let ending = fileParts.splice(-1);

        let newFileName = fileParts.join('.') + appendString + '.' + ending;
        return newFileName;
    }

    static async polyfillIntl(): Promise<void> {
        if (!global.Intl) {
            // No `Intl`, so use and load the polyfill.
            global.Intl = await require('intl');
            require("intl/locale-data/jsonp/en");
        }

        return;
    }

    static isVersionAtLeast = VersionUtil.isVersionAtLeast;

    static getUiValue<T>(state: GlobalState, fieldName: string, defaultValue: T | null = null): T | null {
        if (state.uiFieldValues.fieldValues && state.uiFieldValues.fieldValues[fieldName] && state.uiFieldValues.fieldValues[fieldName]?.value !== undefined) {
            return state.uiFieldValues.fieldValues[fieldName]?.value;
        }
        return defaultValue;
    }

    /**
     * When COM Add-In was opened via text selection, this will always return the textSelection
     */
    static getEmailBody(state: GlobalState, options?: { plainOnly?: boolean, preferUnique?: boolean }): string {
        let emailBody = "";
        if (options?.plainOnly) {
            let uniqueBody = options?.preferUnique ? state.emailData.uniqueBodyPlain : "";
            emailBody = state.emailData.textSelectionPlain || uniqueBody || state.emailData.bodyPlain || "";
        } else {
            let textSelection = state.emailData.textSelectionHtml || state.emailData.textSelectionPlain;
            let uniqueBody = options?.preferUnique ? state.emailData.uniqueBodyHtml || state.emailData.uniqueBodyPlain : "";
            emailBody = textSelection || uniqueBody || state.emailData.bodyHtml || state.emailData.bodyPlain || "";
        }

        return emailBody;
    }

    static getEmailBodyForPrefill(state: GlobalState, setting: EmailBodyPrefillSetting, options?: { plainOnly?: boolean }): string {
        if (setting === "doNotPrefill") {
            if (options?.plainOnly) {
                return state.emailData.textSelectionPlain || "";
            }

            return state.emailData.textSelectionHtml || state.emailData.textSelectionPlain || "";
        }

        return Utilities.getEmailBody(state, {
            plainOnly: options?.plainOnly,
            preferUnique: setting === "preferUniqueEmail"
        });
    }

    static isSharingActive(state: GlobalState): boolean {
        return !state.shareItem.isFullyDeactivated && state.shareItem.isLoggedIn;
    }

    static isJiraVersionHigher(baseVersion: number[], requiredVersion: string): boolean {
        return Utilities.isVersionAtLeast(baseVersion.join('.'), requiredVersion);
    }

    static setLinkTargetInString(text: string): string {
        return text.replace(/<a/gi, '<a target="_blank"');
    }
    static replaceImages(text: string, attachments: Jira.Attachment[]): string {
        attachments.forEach(attachment => {
            text = text.replace(new RegExp(StringUtil.regexpEscape(`<img src="/secure/attachment/${attachment.id}/${attachment.id}_${attachment.filename}"`)), `<img src="${attachment.content}"`)
        })
        return text;
    }

    static getLegacyCompanyId(legacyCompanyToken: string): number {
        // Can this happen or was this just not tested? :D
        if (legacyCompanyToken && legacyCompanyToken.indexOf(":") > 0)
            return parseInt(legacyCompanyToken.split(':')[0]);
        else if (legacyCompanyToken)
            return parseInt((atob(legacyCompanyToken).split(":")[0]));
        else
            return 10;
    }

    static uniqueArray(a: string[]): string[] {
        let seen: {
            [key: string]: boolean;
        } = {};
        return a.filter((item: string) => {
            return seen.hasOwnProperty(item) ? false : (seen[item] = true);
        });
    }

}
